/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Modal, message } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import QuestionForm, {
  defaultQuestionFormValues,
  QuestionFormValues,
} from '~components/atoms/Questions/QuestionForm';
import { addQuestion } from '~src/services/api/questions';

export interface CreateQuestionModalProps extends Pick<ModalProps, 'visible'> {
  shareId: string;
  shareKey?: string;
  onCancel?: () => void;
}

export const CreateQuestionModal: React.FC<CreateQuestionModalProps> = ({
  shareId,
  shareKey,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();

  const [formValues, setFormValues] = useState<QuestionFormValues>(
    defaultQuestionFormValues
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const shareParams = {
    share_id: shareId,
    share_key: shareKey,
  };

  const resetForm = () => {
    setFormValues(defaultQuestionFormValues);
  };

  const handleClose = () => {
    setIsSubmitting(false);
    resetForm();
    onCancel?.();
  };

  const [addQuestionMutation, { isLoading }] = useMutation(addQuestion, {
    onSuccess: async () => {
      queryCache.invalidateQueries([QueryKeys.question, shareParams]);
      queryCache.invalidateQueries(QueryKeys.sharedTenderQuestion);
      queryCache.invalidateQueries(QueryKeys.sharedTenderMyQuestion);
      await queryCache.refetchQueries(QueryKeys.sharedTenderQuestion);
      await queryCache.refetchQueries(QueryKeys.sharedTenderMyQuestion);

      document.dispatchEvent(new CustomEvent('tenderQuestionCreated'));

      message.success(
        intl.formatMessage({ id: 'question-created-successfully' })
      );

      // Close modal and reset form after successful submission
      handleClose();
    },
    onError: (error, item) => {
      setIsSubmitting(false);
      message.error(intl.formatMessage({ id: 'voucher-assigning-failed' }));
      appLog('addQuestionMutation -> item', item);
      appLog('addQuestionMutation -> err', error);
    },
  });

  const isValid = () => {
    if (!formValues.question) {
      return false;
    }

    return true;
  };

  const handleFormChange = (key: keyof QuestionFormValues, newValue: any) => {
    setFormValues((prevForm) => ({
      ...prevForm,
      [key]: newValue,
    }));
  };

  const handleSubmit = async () => {
    if (!isValid() || isLoading || isSubmitting) return;

    setIsSubmitting(true);

    await addQuestionMutation({
      share_id: shareId,
      share_key: shareKey,
      data: formValues,
    });
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({
            id: 'create-question',
          })}
          icon={['far', 'question-circle']}
        />
      }
      okText={intl.formatMessage({ id: 'ask' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered
      okButtonProps={{
        disabled: !isValid() || isLoading || isSubmitting,
        loading: isLoading,
      }}
      onOk={handleSubmit}
      onCancel={handleClose}
      destroyOnClose
      zIndex={2000}
      width={800}
      {...modalProps}>
      <QuestionForm
        formValues={formValues}
        handleValueChange={handleFormChange}
      />
    </Modal>
  );
};
